import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FieldLabel, createUsePuck } from '@puckeditor/core';
import { useIntl } from 'react-intl';
import { Button, Container, Input } from 'reactstrap';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

import { showModalTop } from '../../../actions';
import { APIgetFile } from '../../../api/Files';

import { getVideoPlaceholderElement } from '../../../helpers/PageSections';
import {
  usePageAllowedFileUuids,
  usePageId,
  usePageIsEditing,
} from '../../../helpers/PageIdContext';

import { withLayout } from './PuckLayout';
import formatIntl from './Common/formatIntl';
import { normalizeHydratedPageFile } from './Common/pageFileHydration';
import { getPageFileByUuid } from './Common/pageFiles';
import RadioField from './Common/RadioField';
import SectionHeader from './Common/SectionHeader';
import SectionHeadingField from './Common/SectionHeadingField';

const usePuckSelector = createUsePuck();

const fileShape = PropTypes.shape({
  name: PropTypes.string,
  originalName: PropTypes.string,
  path: PropTypes.string,
  previewUrl: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  videoUrl: PropTypes.string,
  uuid: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  url: PropTypes.string,
  urls: PropTypes.shape({
    preview: PropTypes.string,
    thumbnail: PropTypes.string,
    thumbnailx2: PropTypes.string,
    video: PropTypes.string,
    preview_video: PropTypes.string,
  }),
});

const getYouTubeId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
  ];

  for (let index = 0; index < patterns.length; index += 1) {
    const match = url.match(patterns[index]);
    if (match?.[1]) {
      return match[1];
    }
  }

  return null;
};

const getVimeoEmbedData = (url) => {
  const match = url.match(/(?:vimeo\.com\/(?:video\/)?)(\d+)(?:\/([a-zA-Z0-9]+))?/);

  if (!match?.[1]) {
    return null;
  }

  let hash = match[2] || '';

  if (!hash) {
    try {
      const parsed = new URL(url);
      hash = parsed.searchParams.get('h') || '';
    } catch (error) {
      hash = '';
    }
  }

  return {
    id: match[1],
    hash,
  };
};

const getEmbedUrl = (videoUrl) => {
  if (!videoUrl) return '';

  const youtubeId = getYouTubeId(videoUrl);
  if (youtubeId) {
    return `https://www.youtube.com/embed/${youtubeId}`;
  }

  const vimeoData = getVimeoEmbedData(videoUrl);
  if (vimeoData?.id) {
    const hashParam = vimeoData.hash ? `?h=${vimeoData.hash}` : '';
    return `https://player.vimeo.com/video/${vimeoData.id}${hashParam}`;
  }

  return '';
};

const normalizeHydratedVideoFile = (baseFile = null, resolvedFile = null) => {
  const normalized = normalizeHydratedPageFile(baseFile, resolvedFile);

  if (!normalized) {
    return null;
  }

  const videoUrl = normalized.urls?.preview_video
    || normalized.urls?.video
    || normalized.videoUrl
    || normalized.url
    || null;
  const thumbnailUrl = normalized.thumbnailUrl
    || normalized.urls?.thumbnail
    || normalized.urls?.thumbnailx2
    || normalized.previewUrl
    || normalized.urls?.preview
    || null;

  return {
    ...normalized,
    name: normalized.name || normalized.originalName || '',
    originalName: normalized.originalName || normalized.name || '',
    thumbnailUrl,
    videoUrl,
  };
};

const VideoSourceField = ({
  id,
  name,
  value,
  onChange,
  field,
  readOnly,
}) => (
  <RadioField
    id={id}
    name={name}
    label={field.label}
    value={value || 'workspace'}
    onChange={onChange}
    options={field.options}
    readOnly={readOnly}
  />
);

VideoSourceField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  field: PropTypes.shape({
    label: PropTypes.string,
    options: PropTypes.array,
  }).isRequired,
};

VideoSourceField.defaultProps = {
  id: '',
  name: '',
  value: 'workspace',
  readOnly: false,
};

const VideoFileField = ({
  value,
  onChange,
  field,
  onShowModalTop,
}) => {
  const intl = useIntl();
  const pageId = usePageId();
  const isEditing = usePageIsEditing();
  const allowedFileUuids = usePageAllowedFileUuids();
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const [displayValue, setDisplayValue] = useState(value || null);
  const allowedFileSet = useMemo(
    () => new Set((Array.isArray(allowedFileUuids) ? allowedFileUuids : []).filter(Boolean)),
    [allowedFileUuids],
  );

  useEffect(() => {
    let mounted = true;

    if (!value?.uuid || (!pageId && isEditing)) {
      setDisplayValue(value || null);
      return () => {
        mounted = false;
      };
    }

    const hasThumb = !!(
      value?.thumbnailUrl
      || value?.urls?.thumbnail
      || value?.urls?.thumbnailx2
      || value?.previewUrl
      || value?.urls?.preview
    );
    const hasName = !!(value?.name || value?.originalName);
    const hasVideoSrc = !!(
      value?.videoUrl
      || value?.url
      || value?.urls?.preview_video
      || value?.urls?.video
      || value?.previewUrl
      || value?.urls?.preview
    );

    if (!isEditing && hasThumb && hasName && hasVideoSrc) {
      setDisplayValue(value || null);
      return () => {
        mounted = false;
      };
    }

    const usePageEndpointInEdit = isEditing && pageId && allowedFileSet.has(value.uuid);
    const request = usePageEndpointInEdit || !isEditing
      ? getPageFileByUuid({
        pageId,
        fileUuid: value.uuid,
        fallbackFile: value,
      })
      : APIgetFile(value.uuid).then((res) => (res?.data || value));

    request
      .then((resolved) => {
        if (!mounted) {
          return;
        }

        setDisplayValue(normalizeHydratedVideoFile(value, resolved || value));
      })
      .catch(() => {
        if (mounted) {
          setDisplayValue(value || null);
        }
      });

    return () => {
      mounted = false;
    };
  }, [allowedFileSet, isEditing, pageId, value]);

  if (selectedItem?.props?.source !== 'workspace') {
    return <div className="puck-video-source-file-hidden" />;
  }

  const openModal = () => {
    onShowModalTop('FileSelectModalEnhanced', {
      className: 'modal-select-file modal-select-file-enhanced',
      handleSelectFile: () => {},
      handleFileData: (fileData) => {
        if (!fileData) return;
        onChange({
          name: fileData.original_name || fileData.name || '',
          originalName: fileData.original_name || fileData.name || '',
          path: fileData.path,
          previewUrl: fileData.urls?.preview,
          thumbnailUrl: fileData.urls?.thumbnail
            || fileData.urls?.thumbnailx2
            || fileData.urls?.preview,
          url: fileData.url,
          videoUrl: fileData.urls?.preview_video
            || fileData.urls?.video
            || fileData.url,
          uuid: fileData.uuid,
          width: fileData.width,
          height: fileData.height,
        });
      },
      allowUpload: false,
    });
  };

  const previewThumbnailSrc = displayValue?.thumbnailUrl
    || displayValue?.urls?.thumbnail
    || displayValue?.urls?.thumbnailx2
    || displayValue?.previewUrl
    || displayValue?.urls?.preview
    || '';

  return (
    <div className="puck-file-field-label">
      <FieldLabel label={field.label}>
        {displayValue?.uuid && (
          <div className="puck-file-preview">
            {previewThumbnailSrc ? (
              <img
                src={previewThumbnailSrc}
                alt={intl.formatMessage({ defaultMessage: 'Selected video file' })}
                className="puck-file-preview-img"
              />
            ) : (
              getVideoPlaceholderElement({ className: 'puck-video-field-placeholder' })
            )}
          </div>
        )}
        {(displayValue?.name || displayValue?.originalName) && (
          <div className="mb-2">
            {displayValue?.name || displayValue?.originalName}
          </div>
        )}
        <div className="puck-file-picker-buttons">
          <Button size="sm" color="primary" onClick={openModal} className="w-100 mb-1">
            {displayValue?.uuid
              ? intl.formatMessage({ defaultMessage: 'Change video file' })
              : intl.formatMessage({ defaultMessage: 'Select video file' })}
          </Button>
          {displayValue?.uuid && (
            <Button size="sm" color="secondary" onClick={() => onChange(null)} className="w-100">
              {intl.formatMessage({ defaultMessage: 'Remove' })}
            </Button>
          )}
        </div>
      </FieldLabel>
    </div>
  );
};

VideoFileField.propTypes = {
  value: fileShape,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
  onShowModalTop: PropTypes.func.isRequired,
};

VideoFileField.defaultProps = {
  value: null,
};

const VideoUrlField = ({ value, onChange, field }) => {
  const intl = useIntl();
  const selectedItem = usePuckSelector((s) => s.selectedItem);

  if (selectedItem?.props?.source !== 'url') {
    return <div className="puck-video-source-url-hidden" />;
  }

  return (
    <div className="puck-video-url-field-label">
      <FieldLabel label={field.label}>
        <div className="puck-common-field-hint">
          <strong>
            {intl.formatMessage({
              defaultMessage: 'Accepted formats:',
            })}
          </strong>
          <ul>
            <li>youtube.com/watch?v=...</li>
            <li>youtu.be/...</li>
            <li>youtube.com/embed/...</li>
            <li>youtube.com/shorts/...</li>
            <li>vimeo.com/123456789</li>
            <li>vimeo.com/video/123456789</li>
          </ul>
        </div>
        <Input
          type="text"
          bsSize="sm"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={intl.formatMessage({ defaultMessage: 'https://youtube.com/watch?v=...' })}
        />
      </FieldLabel>
    </div>
  );
};

VideoUrlField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
};

VideoUrlField.defaultProps = {
  value: '',
};

const getAWSAuthenticationParamsFromUrl = (url) => {
  const AWSAuthenticationParamNames = [
    'Policy',
    'Signature',
    'Key-Pair-Id',
  ];

  try {
    const urlObject = new URL(url);
    const urlParams = new URLSearchParams(urlObject.search);
    const params = {};
    AWSAuthenticationParamNames.forEach((param) => {
      if (urlParams.get(param)) {
        params[param] = urlParams.get(param);
      }
    });
    return new URLSearchParams(params).toString();
  } catch (error) {
    return '';
  }
};

const WorkspaceVideoPlayer = ({ src, poster }) => {
  const intl = useIntl();
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    if (!videoRef.current) {
      return () => {};
    }

    const playerInstance = videojs(videoRef.current, {
      controls: true,
      autoplay: false,
      aspectRatio: '16:9',
      playbackRates: [0.5, 1, 1.5, 2],
      notSupportedMessage: intl.formatMessage({
        defaultMessage: 'Your video is being processed, please check back later.',
      }),
      html5: {
        vhs: {
          withCredentials: false,
          maxPlaylistRetries: 2,
          useDevicePixelRatio: true,
          useNetworkInformationApi: true,
          overrideNative: true,
        },
        nativeAudioTracks: videojs.browser.IS_SAFARI,
        nativeVideoTracks: videojs.browser.IS_SAFARI,
        nativeTextTracks: false,
      },
    });

    playerRef.current = playerInstance;

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
      }
      playerRef.current = null;
    };
  }, [intl]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !src) {
      return;
    }

    let resolvedSrc = src;
    if (videojs.browser.IS_IPHONE && process.env.MIX_AWS_CF_DOMAIN) {
      resolvedSrc = resolvedSrc.replace(process.env.MIX_AWS_CF_DOMAIN, `${window.location.host}/hls`);
    }

    let type = 'video/mp4';
    if (resolvedSrc.includes('m3u8')) {
      type = 'application/x-mpegURL';
      const urlParams = getAWSAuthenticationParamsFromUrl(resolvedSrc);

      player.on('xhr-hooks-ready', () => {
        const playerXhrRequestHook = (options) => {
          // eslint-disable-next-line no-param-reassign
          options.beforeSend = (xhr) => {
            const cleanUrl = xhr.url.split('?')[0];
            const authUrl = `${cleanUrl}?${urlParams}`;
            // eslint-disable-next-line no-param-reassign
            xhr.url = authUrl;
            // eslint-disable-next-line no-param-reassign
            xhr.uri = authUrl;
            xhr.open(xhr.method, xhr.uri, xhr.async);
          };
          return options;
        };

        if (urlParams && player.tech()?.vhs?.xhr) {
          player.tech().vhs.xhr.onRequest(playerXhrRequestHook);
        }
      });
    }

    player.reset();
    player.src({ src: resolvedSrc, type });
    player.poster(poster || '');
    player.playbackRates([0.5, 1, 1.5, 2]);
  }, [poster, src]);

  return (
    <div data-vjs-player>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video ref={videoRef} controls className="video-js" />
    </div>
  );
};

WorkspaceVideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  poster: PropTypes.string,
};

WorkspaceVideoPlayer.defaultProps = {
  poster: '',
};

const PuckVideoRender = ({
  sectionHeading,
  source,
  file,
  videoUrl,
  container,
  puck,
}) => {
  const intl = useIntl();
  const pageId = usePageId();
  const allowedFileUuids = usePageAllowedFileUuids();
  const isEditing = puck?.isEditing;
  const [resolvedFile, setResolvedFile] = useState(null);
  const allowedFileSet = useMemo(
    () => new Set((Array.isArray(allowedFileUuids) ? allowedFileUuids : []).filter(Boolean)),
    [allowedFileUuids],
  );

  useEffect(() => {
    let mounted = true;

    if (source !== 'workspace') {
      setResolvedFile(null);
      return () => {
        mounted = false;
      };
    }

    if (!file?.uuid || (!pageId && isEditing)) {
      setResolvedFile(file || null);
      return () => {
        mounted = false;
      };
    }

    const usePageEndpointInEdit = isEditing && pageId && allowedFileSet.has(file.uuid);
    const request = usePageEndpointInEdit || !isEditing
      ? getPageFileByUuid({
        pageId,
        fileUuid: file.uuid,
        fallbackFile: file,
      })
      : APIgetFile(file.uuid).then((res) => (res?.data || file));

    request
      .then((resolved) => {
        if (!mounted) {
          return;
        }

        setResolvedFile(normalizeHydratedVideoFile(file, resolved || file));
      })
      .catch(() => {
        if (mounted) {
          setResolvedFile(file || null);
        }
      });

    return () => {
      mounted = false;
    };
  }, [allowedFileSet, file, isEditing, pageId, source]);

  const fileToRender = resolvedFile || file;
  const embedUrl = useMemo(() => getEmbedUrl(videoUrl || ''), [videoUrl]);
  const sectionTitle = sectionHeading?.title || '';
  const sectionSubtitle = sectionHeading?.subtitle || '';
  const headerAlign = sectionHeading?.align || 'left';
  const hasHeader = !!(sectionTitle || sectionSubtitle);
  const header = (
    <SectionHeader
      title={sectionTitle}
      subtitle={sectionSubtitle}
      align={headerAlign}
    />
  );

  const workspaceSrc = fileToRender?.url || fileToRender?.previewUrl;
  const workspaceVideoSrc = fileToRender?.videoUrl || workspaceSrc;
  const hasWorkspaceVideo = source === 'workspace' && !!workspaceVideoSrc;
  const hasExternalVideo = source === 'url' && !!embedUrl;

  if (!hasWorkspaceVideo && !hasExternalVideo) {
    const emptyMessage = source === 'url'
      ? intl.formatMessage({ defaultMessage: 'No video URL provided' })
      : intl.formatMessage({ defaultMessage: 'No video selected' });
    const emptyPlaceholder = getVideoPlaceholderElement({
      className: 'puck-video-empty-placeholder',
    });

    const emptyContent = (
      <>
        {header}
        <div className="puck-file-preview center">
          {emptyPlaceholder}
        </div>
        {isEditing && <div className="center puck-component-info-text">{emptyMessage}</div>}
      </>
    );

    if (hasHeader) {
      if (container === 'medium') {
        return (
          <div className="custom-page-section">
            <div className="puck-video puck-video-empty">
              <Container className="puck-video-container-medium">
                {emptyContent}
              </Container>
            </div>
          </div>
        );
      }

      if (container === 'container') {
        return (
          <div className="custom-page-section">
            <Container className="puck-video puck-video-empty">
              {emptyContent}
            </Container>
          </div>
        );
      }

      return (
        <div className="custom-page-section">
          <div className="puck-video puck-video-empty">
            {emptyContent}
          </div>
        </div>
      );
    }

    return null;
  }

  const player = hasWorkspaceVideo ? (
    <WorkspaceVideoPlayer
      src={workspaceVideoSrc}
      poster={fileToRender?.thumbnailUrl || fileToRender?.previewUrl || ''}
    />
  ) : (
    <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
      <iframe
        title={intl.formatMessage({ defaultMessage: 'Embedded video' })}
        src={embedUrl}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 0,
        }}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    </div>
  );

  if (container === 'medium') {
    return (
      <div className="custom-page-section">
        <div className="puck-video">
          <Container className="puck-video-container-medium">
            {header}
            {player}
          </Container>
        </div>
      </div>
    );
  }

  if (container === 'container') {
    return (
      <div className="custom-page-section">
        <Container className="puck-video">
          {header}
          {player}
        </Container>
      </div>
    );
  }

  return (
    <div className="custom-page-section">
      <div className="puck-video">
        {header}
        {player}
      </div>
    </div>
  );
};

PuckVideoRender.propTypes = {
  sectionHeading: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    align: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  source: PropTypes.string,
  file: fileShape,
  videoUrl: PropTypes.string,
  container: PropTypes.string,
  puck: PropTypes.object,
};

PuckVideoRender.defaultProps = {
  sectionHeading: null,
  source: 'workspace',
  file: null,
  videoUrl: '',
  container: 'container',
  puck: null,
};

const mapDispatchToProps = (dispatch) => ({
  onShowModalTop: (modalName, params) => dispatch(showModalTop(modalName, params)),
});

const ConnectedVideoFileField = connect(null, mapDispatchToProps)(VideoFileField);

const PuckVideoConfig = {
  fields: {
    sectionHeading: {
      type: 'custom',
      get label() { return formatIntl('Section heading'); },
      render: (props) => <SectionHeadingField {...props} />,
    },
    source: {
      type: 'custom',
      get label() { return formatIntl('Video source'); },
      options: [
        { get label() { return formatIntl('Workspace file'); }, value: 'workspace' },
        { get label() { return formatIntl('YouTube / Vimeo URL'); }, value: 'url' },
      ],
      render: (props) => <VideoSourceField {...props} />,
    },
    file: {
      type: 'custom',
      get label() { return formatIntl('Video file'); },
      render: (props) => <ConnectedVideoFileField {...props} />,
    },
    videoUrl: {
      type: 'custom',
      get label() { return formatIntl('Video URL'); },
      render: (props) => <VideoUrlField {...props} />,
    },
    container: {
      type: 'radio',
      get label() { return formatIntl('Width'); },
      options: [
        { get label() { return formatIntl('Medium'); }, value: 'medium' },
        { get label() { return formatIntl('Container'); }, value: 'container' },
        { get label() { return formatIntl('Full width'); }, value: 'full' },
      ],
    },
  },

  defaultProps: {
    sectionHeading: {
      title: formatIntl('Header'),
      subtitle: formatIntl('Subheader'),
      align: 'left',
    },
    source: 'workspace',
    file: null,
    videoUrl: '',
    container: 'container',
  },

  render: (props) => <PuckVideoRender {...props} />,
};

export default withLayout(PuckVideoConfig);
